import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import {
  TrendingUp, TrendingDown, DollarSign, BarChart3, Activity, AlertTriangle, Building2, Loader2, RefreshCw, Share2,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, AreaChart, Area, Legend, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import ShareInvestorDialog from "../components/ShareInvestorDialog";
import RenewalPipelineCard from "../components/RenewalPipelineCard";
import { fmtUSDate } from "../lib/date";

const fmtUSD = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(2)}%`);
const tickFmt = (n) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

export default function PortfolioPerformance() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/valuation/performance");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (!data) {
    return (
      <DashboardShell>
        <div className="p-8 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading performance…
        </div>
      </DashboardShell>
    );
  }

  const h = data.headline || {};
  const mom = data.month_over_month || [];
  const delta = h.mom_delta;
  const rent = data.rent_collected || {};

  return (
    <DashboardShell>
      <div className="p-8" data-testid="performance-page">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-slate-500">Analytics · CEO mode</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="performance-heading">
              Portfolio Performance
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Your morning-coffee view of the portfolio — current value, month-over-month trend, top/bottom movers, and the properties dragging your weighted cap rate up.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShareOpen(true)}
              className="btn-solid-dark text-sm flex items-center gap-2"
              data-testid="performance-share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share with investors
            </button>
            <button onClick={load} disabled={busy} className="btn-outline-dark text-sm flex items-center gap-2" data-testid="performance-refresh">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
          </div>
        </div>

        <ShareInvestorDialog open={shareOpen} onClose={() => setShareOpen(false)} />

        <RenewalPipelineCard />

        {/* HEADLINE */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white rounded-sm p-6 mb-6" data-testid="performance-headline">
          <div className="flex items-baseline gap-4 flex-wrap">
            <div>
              <div className="overline text-white/60 mb-1">Total portfolio value (income approach)</div>
              <div className="text-5xl font-bold font-mono tracking-tight" data-testid="performance-total-value">
                {fmtUSD(h.current_total_value)}
              </div>
            </div>
            {delta != null && (
              <div className={`text-sm font-mono font-bold ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {delta >= 0 ? "▲" : "▼"} {fmtUSD(Math.abs(delta))}
                <span className="text-xs text-white/50 ml-1">vs last month</span>
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <Kpi label="Weighted cap rate" value={fmtPct(h.current_weighted_cap_rate)} />
            <Kpi label="Total NOI" value={fmtUSD(h.current_total_noi)} />
            <Kpi label="Properties" value={h.property_count} />
            <Kpi label="Rent collected — this month" value={fmtUSD(rent.this_month)} sub={rent.month_label} />
            <Kpi label="Rent collected — YTD" value={fmtUSD(rent.ytd)} />
          </div>
        </div>

        {/* VALUE TREND CHART */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6" data-testid="perf-chart-value">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="overline text-slate-500">Portfolio value by month</div>
              <div className="text-xs text-slate-400">Sourced from snapshot history · auto-populated on the 1st of each month</div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{h.snapshot_months_tracked || 0} months</div>
          </div>
          {mom.length < 2 ? (
            <EmptyChart label="Value trend unlocks after 2+ months of snapshots. Click 'Send me the digest now' on the Valuation → Portfolio view to seed the first data point." />
          ) : (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <AreaChart data={mom} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="valueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0F172A" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                  <YAxis tickFormatter={tickFmt} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={60} />
                  <Tooltip formatter={(v) => [fmtUSD(v), "Value"]} contentStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="total_value" stroke="#0F172A" strokeWidth={2} fill="url(#valueGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* NOI + CAP RATE CHART (dual) */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="perf-chart-noi">
            <div className="overline text-slate-500 mb-2">NOI growth</div>
            {mom.length < 2 ? (
              <EmptyChart label="Needs 2+ months of snapshots." small />
            ) : (
              <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer>
                  <LineChart data={mom} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={tickFmt} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} width={50} />
                    <Tooltip formatter={(v) => [fmtUSD(v), "NOI"]} contentStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="total_noi" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="perf-chart-cap">
            <div className="overline text-slate-500 mb-2">Weighted cap rate compression</div>
            {mom.length < 2 ? (
              <EmptyChart label="Needs 2+ months of snapshots." small />
            ) : (
              <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer>
                  <LineChart data={mom} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip formatter={(v) => [`${v}%`, "Weighted cap"]} contentStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="weighted_cap_rate" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* TOP APPRECIATING / CAP DRAG */}
        <div className="grid md:grid-cols-2 gap-4">
          <MoverCard
            testid="top-appreciating"
            title="Top appreciating"
            icon={<TrendingUp className="w-4 h-4 text-emerald-600" />}
            items={data.top_appreciating || []}
            emptyLabel="Need 2+ monthly snapshots to rank movers."
            metric={(it) => (
              <span className="text-emerald-700 font-mono font-bold text-sm">
                {it.delta >= 0 ? "+" : ""}
                {fmtUSD(it.delta)}
                {it.pct_change != null && <span className="text-[10px] text-emerald-500 ml-1">({it.pct_change >= 0 ? "+" : ""}{it.pct_change}%)</span>}
              </span>
            )}
          />
          <MoverCard
            testid="cap-drag"
            title="Cap-rate drag (weakest compression)"
            icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
            items={data.cap_rate_drag || []}
            emptyLabel="No properties with measurable cap-rate drag."
            metric={(it) => (
              <span className="font-mono font-bold text-sm text-amber-700">
                {fmtPct(it.cap_rate)}
                <span className="text-[10px] text-slate-400 ml-1">NOI {fmtUSD(it.noi)}</span>
              </span>
            )}
          />
        </div>
      </div>
    </DashboardShell>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white/10 rounded-sm px-3 py-2">
      <div className="overline text-white/60">{label}</div>
      <div className="font-mono font-bold text-sm">{value}</div>
      {sub && <div className="text-[10px] text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function EmptyChart({ label, small }) {
  return (
    <div className={`flex items-center justify-center text-center text-slate-400 text-xs font-mono uppercase tracking-widest border border-dashed border-slate-200 rounded-sm ${small ? "py-6" : "py-12"}`}>
      {label}
    </div>
  );
}

function MoverCard({ title, icon, items, metric, emptyLabel, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="overline text-slate-500 mb-2 flex items-center gap-2">
        {icon} {title}
      </div>
      {items.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-400">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it, idx) => (
            <li key={it.property_id || idx} className="py-2 flex items-center gap-3">
              <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate text-sm">{it.property_name || "—"}</div>
                {it.latest_at && (
                  <div className="text-[10px] text-slate-400 font-mono">
                    {fmtUSDate(it.first_at)} → {fmtUSDate(it.latest_at)}
                  </div>
                )}
              </div>
              {metric(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
